import {
  AspectRatio,
  Box,
  Image as ChakraImage,
  ImageProps,
  ImgProps,
  Skeleton,
  StackProps,
} from '@chakra-ui/react';
import { useState } from 'react';
import { getImageUrl } from '@habity/services';
import { ImageType, ImageVariant, ProjectImageType, TODO } from '@habity/types';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination, Navigation } from 'swiper/modules';
import { SwiperNavButtons } from './NavButtons';
import { Swiper as SwiperType } from 'swiper/types';
import { ImageCardGradientOverlay } from './GradientOverlay';
import { STYLES } from '~/components/utility/utils';

interface GalleryImageProps extends ImgProps {
  isBlurred?: boolean;
}

const GalleryImage = ({
  src,
  srcSet,
  isBlurred = false,
}: GalleryImageProps) => {
  return (
    <ChakraImage
      loading="lazy"
      cursor="pointer"
      src={typeof src === 'string' ? src : undefined}
      srcSet={srcSet}
      objectFit="cover"
      fallbackSrc="/eo/images/eo-home-missing-img.jpg"
      fallbackStrategy="onError"
      style={isBlurred ? STYLES.imageBlur : {}}
    />
  );
};


interface GalleryProps {
  variant?: 'home' | 'project';
  images: Array<ImageType | ProjectImageType>;
  onClick?: () => void;
  onGalleryOpen?: () => void;
  imageVariant: ImageVariant;
  aspectRatio?: number;
  rootProps?: StackProps;
  imageProps?: ImageProps;
  borderTopRadius?: string;
  borderBottomRadius?: string;
  navButtonsSize?: string;
  maxSlides?: number;
  pagination: TODO;
  isLoading: boolean;
  isBlurred?: boolean;
}

export const Gallery = ({
  images: inputImages,
  onClick,
  aspectRatio = 1,
  rootProps,
  imageVariant,
  borderTopRadius = '4',
  borderBottomRadius = '4',
  maxSlides,
  pagination,
  isLoading,
  isBlurred = false,
}: GalleryProps) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [loaded, setLoaded] = useState(false);

  const images = maxSlides ? inputImages?.slice(0, maxSlides) : inputImages;

  return (
    <Box
      position="relative"
      sx={{
        _hover: {
          '.swiper-nav-btns button': {
            display: 'inline-flex',
          },
        },
      }}
      overflow="hidden"
      borderTopRadius={borderTopRadius}
      borderBottomRadius={borderBottomRadius}
      {...rootProps}
    >
      {/* Multiple images = Swiper. Single image = No swiper */}
      {images?.length && images?.length > 1 ? (
        <AspectRatio ratio={aspectRatio}>
          <Skeleton
            startColor="blackAlpha.50"
            endColor="blackAlpha.100"
            isLoaded={!isLoading && loaded}
          >
            <Swiper
              onSwiper={() => setLoaded(true)}
              style={
                {
                  '--swiper-pagination-color': 'white',
                  borderRadius: `${borderTopRadius} ${borderTopRadius} ${borderBottomRadius} ${borderBottomRadius}`,
                } as TODO
              }
              pagination={pagination}
              onSlideChange={(swiper: SwiperType) =>
                setCurrentSlide(swiper.activeIndex)
              }
              lazyPreloaderClass="swiper-lazy-preloader"
              lazyPreloadPrevNext={2}
              modules={[Pagination, Navigation]}
            >
              {images?.map((image, index: number) => {
                const imageUrl = getImageUrl(image as any, imageVariant);
                return (
                  <SwiperSlide
                    key={`${image?.id}-${index}`}
                    onClick={onClick}
                    style={{ position: 'relative' }}
                  >
                    <GalleryImage
                      src={imageUrl}
                      srcSet={`${imageUrl} 3x`}
                      isBlurred={isBlurred}
                    />
                    <ImageCardGradientOverlay />
                  </SwiperSlide>
                );
              })}
              <SwiperNavButtons
                currentSlide={currentSlide}
                maxSlides={images?.length || 0}
              />
            </Swiper>
          </Skeleton>
        </AspectRatio>
      ) : images?.length && images?.length === 1 ? (
        <AspectRatio ratio={aspectRatio}>
          <GalleryImage
            src={getImageUrl(images[0] as any, imageVariant)}
            srcSet={`${getImageUrl(images[0] as any, imageVariant)} 3x`}
            borderTopRadius={borderTopRadius}
            borderBottomRadius={borderBottomRadius}
            isBlurred={isBlurred}
          />
        </AspectRatio>
      ) : (
        // No images - show fallback
        <AspectRatio ratio={aspectRatio}>
          {isLoading ? (
            <Skeleton
              startColor="blackAlpha.50"
              endColor="blackAlpha.100"
              isLoaded={false}
            />
          ) : (
            <GalleryImage
              src="/eo/images/eo-home-missing-img.jpg"
              borderTopRadius={borderTopRadius}
              borderBottomRadius={borderBottomRadius}
            />
          )}
        </AspectRatio>
      )}
    </Box>
  );
};
